import React, { useEffect, useState } from 'react';
import { CalendarCheck, Clock, Loader2, Package, ShoppingCart, TrendingUp, User, Wrench } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { getDashboard, seedData, updateMaintenanceSettings } from '../../config';
import { useAuth } from '../../context/AuthContext';

export default function AdminDashboard() {
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);

  const canManageProducts = hasPermission('products:manage');
  const canUpdateMaintenance = hasPermission('maintenance:update');

  const fetchData = async () => {
    try {
      const response = await getDashboard();
      setData(response);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSeed = async () => {
    if (!canManageProducts) return;
    setSeeding(true);
    try {
      await seedData();
      await fetchData();
    } finally {
      setSeeding(false);
    }
  };

  const handleToggleMaintenance = async () => {
    if (!canUpdateMaintenance) return;
    setMaintenanceSaving(true);
    try {
      await updateMaintenanceSettings(!data?.maintenance?.enabled);
      await fetchData();
    } finally {
      setMaintenanceSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Tableau de bord">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-secondary animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const stats = data?.stats || {};
  const maintenance = data?.maintenance || { enabled: false, message: '' };

  return (
    <AdminLayout title="Tableau de bord">
      <div className="space-y-6">
        <div className={`rounded-sm border p-4 ${maintenance.enabled ? 'bg-secondary/10 border-secondary/30' : 'bg-white border-slate-200'}`}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${maintenance.enabled ? 'bg-secondary/10 text-secondary' : 'bg-slate-100 text-slate-600'}`}>
                  <Wrench className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-white font-semibold">Mode maintenance boutique</p>
                  <p className="text-xs text-slate-600">Bloque les nouvelles reservations et affiche un bandeau discret sur le site public.</p>
                </div>
              </div>
              <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${maintenance.enabled ? 'bg-secondary/10 text-secondary' : 'bg-emerald-50 text-emerald-600'}`}>
                <span className={`w-2 h-2 rounded-full ${maintenance.enabled ? 'bg-secondary' : 'bg-emerald-400'}`} />
                {maintenance.enabled ? 'Maintenance active' : 'Reservations ouvertes'}
              </div>
              {maintenance.enabled && maintenance.message && <p className="text-xs text-secondary/80">{maintenance.message}</p>}
            </div>

            {canUpdateMaintenance && (
              <button
                type="button"
                onClick={handleToggleMaintenance}
                disabled={maintenanceSaving}
                className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${maintenance.enabled ? 'bg-slate-100 text-white hover:bg-white/15' : 'bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white'}`}
              >
                {maintenanceSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
                {maintenance.enabled ? 'Desactiver la maintenance' : 'Activer la maintenance'}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Produits', value: stats.total_products || 0, icon: Package },
            { label: 'Disponibles', value: stats.available || 0, icon: TrendingUp },
            { label: 'Reserves', value: stats.reserved || 0, icon: CalendarCheck },
            { label: 'Vendus', value: stats.sold || 0, icon: ShoppingCart },
          ].map((item) => (
            <div key={item.label} className="bg-white rounded-sm border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-slate-600">{item.label}</span>
                <item.icon className="w-4 h-4 text-secondary" />
              </div>
              <p className="font-heading text-2xl uppercase tracking-tight font-bold text-primary">{item.value}</p>
            </div>
          ))}
        </div>

        {canManageProducts && stats.total_products === 0 && (
          <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-6 text-center">
            <p className="text-secondary mb-3">Aucun produit dans le catalogue</p>
            <button
              type="button"
              onClick={handleSeed}
              disabled={seeding}
              className="bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-6 py-2.5 text-sm hover:bg-secondary transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {seeding && <Loader2 className="w-4 h-4 animate-spin" />}
              {seeding ? 'Ajout en cours...' : 'Ajouter des produits de demonstration'}
            </button>
          </div>
        )}

        <div>
          <h2 className="font-heading font-semibold text-primary text-lg uppercase tracking-wider mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-secondary" />
            Reservations du jour ({stats.today_reservations || 0})
          </h2>

          {data?.recent_reservations?.length > 0 ? (
            <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Produit</th>
                      <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Client</th>
                      <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Telephone</th>
                      <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_reservations.map((reservation) => (
                      <tr key={reservation.id} className="border-b border-slate-200 last:border-0">
                        <td className="px-4 py-3 text-primary font-medium">{reservation.product_name}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5 text-slate-500" />
                            <span className="text-slate-700">{reservation.first_name} {reservation.last_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{reservation.phone}</td>
                        <td className="px-4 py-3 text-slate-700">{reservation.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-sm border border-slate-200 p-8 text-center">
              <p className="text-slate-500 text-sm">Aucune reservation aujourd'hui</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
